/**
 * "Office contact for escalations" — who a carer's Notify office reaches.
 *
 * The plan's FHIR R5 `custodian` defaults to whoever last saved it (Ed's
 * latest-author rule). This module is the affordance for overriding that: a
 * planner names someone else, and the choice is marked `_custodianPinned` so
 * no later save silently moves responsibility back. See ./plan-custodian for
 * the write rule and the standard's fact/choice split.
 *
 * WHO IS PICKABLE (Ed's "alternative person"): anyone holding a live office
 * role — `planner`, `admin` or `librarian`. Deliberately NOT every `_person`:
 * the point of the escalation is that somebody can sign in and act on it, and
 * an office contact who cannot open Outcome Monitoring is a dead letter that
 * looks like a delivery. Carers are excluded for the same reason — the carer
 * is the one raising the flag.
 *
 * ☠️ `_practitionerRole.code[].coding[].code` cannot be filtered server-side —
 * Firestore has no query over a field inside an array of maps
 * (feedback_firestore_array_of_maps_query_gotcha). So we filter `active` in
 * the query and the role code in the browser, then resolve names for the
 * office subset only (a few dozen of ~545 roles), not for every carer.
 */
import { collection, getDocs, query, where } from "firebase/firestore";

import { db } from "../firebase";
import { loadPersonSummaries, STC_ACCESS_ROLE_SYSTEM } from "../admin/loaders";
import { escapeHtml } from "../utils/html";

/** Role codes that can hold an escalation. Carer is excluded by design. */
export const OFFICE_ROLE_CODES: ReadonlySet<string> = new Set([
  "planner",
  "admin",
  "librarian",
]);

export interface OfficeContact {
  personDocId: string;
  name: string | null;
  /** ☠️ No email ⇒ the escalation degrades to support@ alone. Shown, not hidden. */
  email: string | null;
  /** The office role that made them pickable, for the hint line. */
  role: string;
}

// ── pure: reading a role document ───────────────────────────────────────────

/**
 * The access-role code on a `_practitionerRole` doc, or null.
 *
 * Mirrors `admin/loaders.extractRole`, but returns the raw string rather than
 * a narrowed `AccessRole`: this module's question is "is it an office role",
 * and an unknown future code should simply not match rather than crash.
 */
export function roleCodeOf(roleDoc: Record<string, unknown>): string | null {
  const entries = roleDoc.code;
  if (!Array.isArray(entries)) return null;
  for (const entry of entries) {
    const coding = (entry as { coding?: unknown })?.coding;
    if (!Array.isArray(coding)) continue;
    for (const c of coding) {
      const cc = c as { system?: unknown; code?: unknown };
      if (cc?.system === STC_ACCESS_ROLE_SYSTEM && typeof cc.code === "string") {
        return cc.code;
      }
    }
  }
  return null;
}

/**
 * The `_person` doc id a role authorises, or null.
 *
 * ☠️ Native DocumentReference only. A flattened string is tolerated on READ
 * because historic docs carry them, but nothing here ever writes one back
 * (feedback_firestore_documentreference_no_string_serialization).
 */
export function personIdOf(roleDoc: Record<string, unknown>): string | null {
  const ref = (roleDoc.practitioner as { reference?: unknown } | undefined)?.reference;
  if (ref && typeof ref === "object" && typeof (ref as { id?: unknown }).id === "string") {
    return (ref as { id: string }).id;
  }
  if (typeof ref === "string" && ref.includes("/")) return ref.split("/").pop() || null;
  return null;
}

/**
 * `(personDocId → role code)` for every office-capable role in `docs`.
 *
 * A person may hold several roles (planner AND admin); first match wins,
 * which is enough for a hint line. An ended grant is excluded here rather
 * than in the query because `period.end` is a date comparison Firestore
 * cannot combine with the `active` equality without a composite index.
 */
export function officeRoleHolders(
  docs: Iterable<Record<string, unknown>>,
  now: Date = new Date(),
): Map<string, string> {
  const out = new Map<string, string>();
  for (const d of docs) {
    if (d.active === false) continue;
    const end = (d.period as { end?: unknown } | undefined)?.end;
    if (typeof end === "string" && end && new Date(end) <= now) continue;
    const code = roleCodeOf(d);
    if (!code || !OFFICE_ROLE_CODES.has(code)) continue;
    const pid = personIdOf(d);
    if (pid && !out.has(pid)) out.set(pid, code);
  }
  return out;
}

/** Name-ordered, with the nameless last — a blank row is unpickable in practice. */
export function sortContacts(contacts: OfficeContact[]): OfficeContact[] {
  return [...contacts].sort((a, b) => {
    if (!a.name) return b.name ? 1 : 0;
    if (!b.name) return -1;
    return a.name.localeCompare(b.name);
  });
}

// ── loading (reads Firestore; cached for the session) ───────────────────────

let cache: OfficeContact[] | null = null;

/** Drop the cached directory — exported for tests and for a post-grant refresh. */
export function resetOfficeContactCache(): void {
  cache = null;
}

/**
 * Everyone who could hold an escalation, names resolved.
 *
 * Cached like Chase's carer directory: a few dozen names change rarely, and
 * the sweep over `_practitionerRole` is the expensive half.
 */
export async function loadOfficeContacts(): Promise<OfficeContact[]> {
  if (cache) return cache;
  const snap = await getDocs(
    query(collection(db, "_practitionerRole"), where("active", "==", true)),
  );
  const holders = officeRoleHolders(snap.docs.map((d) => d.data()));
  const summaries = await loadPersonSummaries(holders.keys());
  cache = sortContacts(
    Array.from(holders, ([personDocId, role]) => ({
      personDocId,
      role,
      name: summaries.get(personDocId)?.name ?? null,
      email: summaries.get(personDocId)?.email ?? null,
    })),
  );
  return cache;
}

/**
 * The display name for a pinned contact, or null if we cannot name them.
 *
 * Null is a real answer, not just a miss: a custodian whose office role has
 * since been revoked is no longer in the directory, and the builder should
 * say so plainly rather than render a blank where a name belongs.
 */
export async function contactName(personDocId: string): Promise<string | null> {
  try {
    const all = await loadOfficeContacts();
    return all.find((c) => c.personDocId === personDocId)?.name ?? null;
  } catch {
    return null;
  }
}

// ── the modal ───────────────────────────────────────────────────────────────

export type ContactChoice =
  | { kind: "pick"; personDocId: string; name: string | null }
  | { kind: "clear" };

/**
 * Modal picker. Resolves with a choice, or null on dismiss (Escape, backdrop,
 * Cancel) — the same contract as Chase's `pickCarer`, whose shape and CSS
 * classes this reuses rather than inventing a second modal idiom.
 *
 * "Clear" is always offered when a contact is pinned, so pinning is never a
 * one-way door: clearing returns the plan to the default (whoever saves next
 * becomes custodian).
 */
export function pickOfficeContact(
  currentPersonDocId: string | null,
): Promise<ContactChoice | null> {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.className = "chase-modal-overlay";
    overlay.innerHTML = `
      <div class="chase-modal" role="dialog" aria-label="Office contact for escalations">
        <h3>Office contact for escalations</h3>
        <div class="chase-modal-hint" style="margin-bottom:8px;">
          When a carer taps “Notify office” on this plan, this is who hears about it.
          Leave it unset and it follows whoever last saved the plan. support@ is
          copied either way.
        </div>
        <input class="chase-modal-search" type="text" placeholder="Start typing a name…" autocomplete="off">
        <div class="chase-modal-list"><div class="chase-modal-hint">Loading office staff…</div></div>
        <div class="chase-modal-foot">
          ${currentPersonDocId ? '<button class="chase-modal-clear">Clear — follow the last saver</button>' : ""}
          <button class="chase-modal-cancel">Cancel</button>
        </div>
      </div>`;
    document.body.appendChild(overlay);

    const done = (choice: ContactChoice | null) => {
      overlay.remove();
      document.removeEventListener("keydown", onKey);
      resolve(choice);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") done(null);
    };
    document.addEventListener("keydown", onKey);
    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) done(null);
    });
    overlay.querySelector(".chase-modal-cancel")?.addEventListener("click", () => done(null));
    overlay.querySelector(".chase-modal-clear")?.addEventListener("click", () => done({ kind: "clear" }));

    const input = overlay.querySelector<HTMLInputElement>(".chase-modal-search")!;
    const list = overlay.querySelector<HTMLElement>(".chase-modal-list")!;
    input.focus();

    void loadOfficeContacts()
      .then((all) => {
        const render = () => {
          const q = input.value.trim().toLowerCase();
          // Unlike Chase's carer search (thousands of names, so it starts
          // empty), the office list is short enough to show in full — a
          // planner who doesn't know the name can still browse it.
          const hits = (q
            ? all.filter((c) => (c.name ?? "").toLowerCase().includes(q))
            : all
          ).slice(0, 50);
          list.innerHTML = hits.length
            ? hits.map((c) => `<button class="chase-hit" data-pid="${escapeHtml(c.personDocId)}">${
                escapeHtml(c.name ?? c.personDocId)
              }<span class="chase-soft"> · ${escapeHtml(c.role)}${
                c.personDocId === currentPersonDocId ? " · current" : ""
              }${
                // ☠️ Say it here rather than let the escalation quietly
                // degrade: no address on file means support@ alone.
                c.email ? "" : " · no email on file"
              }</span></button>`).join("")
            : `<div class="chase-modal-hint">${
                q ? "Nobody in the office matches that." : "No office staff found."
              }</div>`;
          list.querySelectorAll<HTMLButtonElement>(".chase-hit").forEach((b) =>
            b.addEventListener("click", () => {
              const pid = b.dataset.pid;
              if (!pid) return;
              done({
                kind: "pick",
                personDocId: pid,
                name: all.find((c) => c.personDocId === pid)?.name ?? null,
              });
            }),
          );
        };
        input.addEventListener("input", render);
        render();
      })
      .catch((e) => {
        // Failing to load the directory must not block the plan: the modal
        // says so and dismisses to no change, leaving the existing contact.
        list.innerHTML = `<div class="chase-modal-hint">Couldn't load the office list — ${escapeHtml(
          e instanceof Error ? e.message : String(e),
        )}</div>`;
      });
  });
}
